import { captureExceptionWithSentry } from '../../../../sentry'
import apiFetch, { ApiResponse } from '../../../../common/apiFetch'
import * as React from 'react'
import {
	Alert,
	Button,
	ButtonGroup,
	ButtonToolbar,
	Checkbox,
	Col,
	ControlLabel,
	FormControl,
	FormGroup,
	HelpBlock,
	OverlayTrigger,
	Panel,
	Row,
	Tooltip,
} from 'react-bootstrap'
import { InfoPopup } from '../../../../common/InfoPopup'
import LoadIndicator from '../../../../common/LoadIndicator'
import StaticModal from '../../../../common/StaticModal'
import { validateUrl } from '../../../../common/Url'
import Location from '../../adLocations/Location'
import { Variation, VariationLimits } from '../articlesContainer'
import VariationItem from './VariationItem'
import './VariationPanel.scss'
import { AiVariationSuggestions } from './manageAiVariationsButton/aiVariationTypes'
import VideoPlayer from '../../../../common/components/videos/videoPlayer'
import { AdFormats } from '../../adFormat/adFormat'
import Icon, { IconType } from '../../../../common/components/icon/Icon'

interface VariationPanelProps {
	articleUid: string
	variation: Variation
	updateVariationField?: (
		uid: string,
		variationNum: number,
		prop: keyof Variation,
		value: any,
		instant?: boolean,
	) => void
	handleCloseModal: () => void
	handleShowModal: (modal: string) => void
	showModal: boolean
	modalName: string
	modalTitle: string
	removeVariation?: (articleUid: string, variationNo: number, instant?: boolean) => void
	duplicateVariation?: (articleUid: string, variationNo: number, instant?: boolean) => void
	selectedLocations?: Location[]
	campaignIsActive?: boolean
	instant?: boolean
	client?: {
		id: number
		org: string
	}
	campaignId?: number
	articleId?: number
	onDashboard?: boolean
	managingAiSuggestions?: boolean
	approveOrDisapproveAiSuggestion?: (variation: Variation, approve: boolean) => void
	allSuggestions?: AiVariationSuggestions
	adFormat: AdFormats
	readonly?: boolean
}

interface VariationPanelState {
	editing?: boolean
	showComment?: boolean
	focusedElement?: string
	imageWidth?: number
	imageHeight?: number
	imgError?: boolean
	imageLoading?: boolean
	videoLoading?: boolean
	videoError: string | null
	managingAiSuggestions?: boolean
	closeSuggestionPopupExcept: keyof Variation | undefined // whenever this changes, all VariationItems will close their ai suggestion popups
	showAiImageEditor: boolean
}

// array of these is stored into local storage to keep track of which comments have been seen
interface SeenComment {
	added: number
	variationId: number
	comment: string
}

export interface Image {
	id: string
	image_id: string
	width: string
	height: string
	path: string
	created_at: string
}

interface Video {
	id: string
	width: number
	height: number
	path: string
	type: string
}

const probablyClickTrackers = ['ad.doubleclick.net/ddm/trackclk', 'track.adform.net/C/']

const knownClickTrackerDomains = ['track.adform.net', 'ad.doubleclick.net', 'pubads.g.doubleclick.net']

export const validateImpressionUrl = (impressionUrl: string) => {
	if (!impressionUrl) {
		return 'success'
	}
	const impressionUrls = impressionUrl.split(',')
	for (const impUrl of impressionUrls) {
		if (!impUrl || impUrl.trim() === '') {
			continue
		}
		if (!validateUrl(impUrl.trim(), true)) {
			return 'error'
		}
		if (!containsGdprMacros(impUrl)) {
			return 'error'
		}
	}
	return 'success'
}

export const validateClickUrl = (clickUrl: string) => {
	if (validateUrl(clickUrl.trim())) {
		const url = new URL(clickUrl.trim())
		if (knownClickTrackerDomains.includes(url.hostname) && !containsGdprMacros(clickUrl)) {
			return 'error'
		}
		return 'success'
	}
	return 'error'
}

const containsGdprMacros = (url: string) => {
	const lowerCaseUrl = url.toLowerCase()
	const regex = /\$\{gdpr_consent_\d+\}/i
	return lowerCaseUrl.includes('${gdpr}') && regex.test(lowerCaseUrl)
}

class VariationPanel extends React.PureComponent<VariationPanelProps, VariationPanelState> {
	public constructor(props: VariationPanelProps) {
		super(props)
		this.state = {
			showComment: false,
			videoError: null,
			managingAiSuggestions: props.managingAiSuggestions,
			closeSuggestionPopupExcept: undefined,
			showAiImageEditor: false,
		}
	}

	public componentDidMount() {
		// image from the scraper
		if (
			!this.props.variation.suggestion &&
			this.props.variation.image &&
			!this.props.variation.image_id &&
			!this.props.onDashboard
		) {
			this.handleImage(this.props.variation.image, true)
		}
	}

	private getSuggestions(fieldName: keyof AiVariationSuggestions): string[] {
		return this.props.allSuggestions ? this.props.allSuggestions[fieldName] : []
	}

	private handleCloseAllAiPopupsExcept(field: keyof Variation) {
		this.setState({
			closeSuggestionPopupExcept: field,
		})
	}

	private setValue = (name: keyof Variation, value: string | number[]) => {
		if (this.props.updateVariationField) {
			this.props.updateVariationField(
				this.props.articleUid,
				this.props.variation.variation_no,
				name,
				value,
				this.props.instant,
			)

			// mark comment as seen so we don't see our own edits as "new comments"
			if (name === 'comment') {
				this.markCommentAsSeen(value as string)
			}
		}
	}

	private removeVariation = () => {
		if (this.props.removeVariation) {
			this.props.removeVariation(this.props.articleUid, this.props.variation.variation_no, this.props.instant)
		}
	}
	private panelHeader = () => {
		const deleteIsAllowed =
			!this.props.readonly &&
			(this.props.variation.impressions === undefined || this.props.variation.impressions === 0) &&
			(!this.props.variation.is_active || !this.props.campaignIsActive)
		return (
			<Row>
				<Col xs={5}>
					<span>
						{deleteIsAllowed && this.props.removeVariation && (
							<>
								<OverlayTrigger
									trigger={['hover', 'focus']}
									placement="bottom"
									overlay={<Tooltip id={'delete'}>Delete</Tooltip>}
								>
									<i
										className="far fa-trash-alt"
										onClick={this.removeVariation}
										style={{ cursor: 'pointer' }}
									/>
								</OverlayTrigger>{' '}
							</>
						)}
						{this.props.managingAiSuggestions ? 'Suggestion' : 'Variation'}&nbsp;
						{this.props.variation.variation_no}
					</span>
				</Col>
				<Col xs={7}>{!this.props.readonly && this.renderIcons()}</Col>
			</Row>
		)
	}
	private setTheme = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		this.setValue('theme', e.currentTarget.value)
	}
	private toggleEditable = (focused?: string) => {
		// if we're in read-only mode, don't allow editing
		if (this.props.readonly) {
			return
		}

		this.setState((prevState) => ({
			editing: !prevState.editing,
			focusedElement: focused,
		}))
	}

	readonly seenAiCommentsLocalStorageKey = 'seenAiComments'

	private getSeenComments = (): SeenComment[] => {
		return JSON.parse(localStorage.getItem(this.seenAiCommentsLocalStorageKey) || '[]') as SeenComment[]
	}

	private isCommentNew = (): boolean => {
		try {
			const seenComment = this.getSeenComments().find((sc) => sc.variationId === this.props.variation.id)
			return !seenComment || seenComment.comment !== this.props.variation.comment
		} catch (err) {
			// something went wrong? no worries, simply show comments as not new
			return false
		}
	}

	private markCommentAsSeen = (textContent: string) => {
		const otherComments = this.getSeenComments()
			.filter((sc) => sc.added > Date.now() - 1000 * 60 * 60 * 24 * 31) // track status for only 31 days
			.filter((sc) => sc.variationId !== this.props.variation.id)

		const thisComment: SeenComment = {
			variationId: this.props.variation.id!,
			added: Date.now(),
			comment: textContent,
		}

		localStorage.setItem(this.seenAiCommentsLocalStorageKey, JSON.stringify([thisComment, ...otherComments]))
	}

	private toggleComment = () => {
		// if we're making thos comment field visible ...
		if (!this.state.showComment) {
			// ... then mark item as seen into local storage
			this.markCommentAsSeen(this.props.variation.comment ?? '')
		}
		this.setState({
			showComment: !this.state.showComment,
		})
	}

	private renderIcons = () => {
		const iconStyle = {
			paddingLeft: '8px',
			paddingRight: '8px',
			cursor: 'pointer',
		}
		const renderIcon = (faIconClass: string, popOverText: string, iconAction: string) => {
			let handleIconClick = () => {
				this.props.handleShowModal(iconAction)
			}
			switch (iconAction) {
				case 'ClickTargetModal':
				case 'ImpressionPixelModal':
				case 'SelectMediasModal':
					handleIconClick = () => {
						this.props.handleShowModal(iconAction)
					}
					break
				case 'duplicate':
					handleIconClick = () => {
						this.props.duplicateVariation!(
							this.props.articleUid,
							this.props.variation.variation_no,
							this.props.instant,
						)
					}
					break
				case 'toggleEdit':
					handleIconClick = () => {
						this.toggleEditable()
					}
					break
			}
			return (
				<OverlayTrigger
					trigger={['hover', 'focus']}
					placement="bottom"
					overlay={<Tooltip id={'link'}>{popOverText}</Tooltip>}
				>
					<span style={iconStyle} onClick={handleIconClick}>
						<i className={faIconClass} />
					</span>
				</OverlayTrigger>
			)
		}

		return (
			<span className="pull-right">
				{renderIcon('fas fa-link text-primary', 'Edit Click Target URL', 'ClickTargetModal')}
				{renderIcon(
					'far fa-chart-bar text-primary',
					'Edit Impression Tracker Pixel(s)',
					'ImpressionPixelModal',
				)}
				{this.props.duplicateVariation &&
					renderIcon('far fa-copy text-primary', 'Duplicate Variation', 'duplicate')}
				{this.props.selectedLocations &&
					this.props.selectedLocations.length > 0 &&
					renderIcon('far fa-newspaper text-primary', 'Select Medias', 'SelectMediasModal')}
				{!this.props.readonly &&
					renderIcon('fas fa-edit text-success', this.state.editing ? 'Preview' : 'Edit', 'toggleEdit')}
			</span>
		)
	}
	private renderThemeRow = () => {
		return (
			<Row style={{ marginTop: '10px' }}>
				<Col xs={12}>
					{this.props.variation.approved && (
						<div className="aiSuggestionTag approvedTag">
							{this.state.editing ? 'Approved' : 'Will be added to campaign'}
						</div>
					)}
					{this.props.variation.dismissed && (
						<div className="aiSuggestionTag dismissedTag">
							{this.state.editing ? 'Dismissed' : 'Will be removed'}
						</div>
					)}
					<VariationItem
						name="theme"
						type="text"
						placeholder="THEME"
						value={this.props.variation.theme}
						onChange={this.setTheme}
						editable={this.state.editing}
						textElement="strong"
						toggleEditable={this.toggleEditable}
						focus={this.state.focusedElement === 'theme'}
						maxLength={VariationLimits.theme}
						suggestion={this.props.variation.suggestion}
						readonly={this.props.readonly}
						// minLength={VariationLimits.minTheme}
					/>
				</Col>
			</Row>
		)
	}
	private setImage = (e: any) => {
		e.preventDefault()

		if (!e.target.files[0]) {
			return
		}

		const fileReader = new FileReader()
		fileReader.addEventListener('loadend', () => {
			const result = fileReader.result
			if (typeof result !== 'string') {
				return
			}
			this.handleImage(result)
		})

		fileReader.readAsDataURL(e.target.files[0])
	}

	private handleImage = async (image: string, checkOnly = false) => {
		this.setState({
			imgError: false,
			imageLoading: true,
		})

		this.saveImage(image, checkOnly)
	}

	private saveImage = async (image: string, checkOnly = false) => {
		if (this.state.managingAiSuggestions && checkOnly) {
			return false
		}

		this.setState({
			imgError: false,
		})
		const onlyCheckImageValidity = checkOnly ? '?validate=true' : ''
		const data = {
			image,
			type: 'ad',
		}
		try {
			const imageSaveResult = await apiFetch(`/images${onlyCheckImageValidity}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(data),
			})

			if (imageSaveResult.ok) {
				if (checkOnly) {
					this.setValue('image', image)
					this.setValue('imgformat', 'base64')
					this.setValue('image_id', '')

					this.setState({ imageLoading: false })
					return true
				}
				const response = await imageSaveResult.json()
				const images = response.data
				let shownImage = images[0]
				let imageHeight = +shownImage.height
				let imageWidth = +shownImage.width
				for (const img of images) {
					if (+img.width > +shownImage.width) {
						shownImage = img
					}
					if (+img.width >= 1200 && +img.height >= 628) {
						imageHeight = +img.height
						imageWidth = +img.width
						break
					}
				}
				this.setState({
					imageHeight,
					imageWidth,
					imgError: false,
				})
				this.setValue('image_id', shownImage.id)
				this.setValue('image', shownImage.path)
			} else {
				this.setValue('image', '')
				this.setState({
					imageHeight: undefined,
					imageWidth: undefined,
					imgError: true,
				})
			}
		} catch (error) {
			captureExceptionWithSentry(error)
			this.setValue('image', '')
			this.setState({
				imageHeight: undefined,
				imageWidth: undefined,
				imgError: true,
			})
		}

		this.setState({ imageLoading: false })
	}

	private renderVideo = () => {
		return (
			<FormGroup
				controlId={`image-${this.props.articleUid}-${this.props.variation.variation_no}`}
				validationState={this.props.variation.videoPath ? 'success' : 'error'}
			>
				<Col md={12}>
					<ControlLabel style={{ width: '100%' }}>
						{this.props.variation.videoPath ? (
							<>
								<VideoPlayer url={this.props.variation.videoPath} />
								{this.state.editing && (
									<Panel
										style={{
											cursor: 'pointer',
											width: '100%',
											paddingTop: '10px',
											minHeight: '50px',
											maxHeight: '50px',
										}}
										className="text-center"
									>
										Change Video
									</Panel>
								)}
							</>
						) : (
							<Panel
								style={{
									cursor: 'pointer',
									width: '100%',
									paddingTop: '80px',
									minHeight: '200px',
									maxHeight: '200px',
								}}
								className="text-center"
							>
								{this.state.videoLoading ? <LoadIndicator /> : 'Upload a Video'}
							</Panel>
						)}
					</ControlLabel>
					<FormControl
						style={{
							opacity: 0,
							position: 'absolute',
							pointerEvents: 'none',
							width: '1px',
							height: '1px',
						}}
						type="file"
						placeholder="Upload a video"
						onChange={this.setVideo}
						data-test="video_upload"
					/>
				</Col>
			</FormGroup>
		)
	}

	private setVideo = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		e.preventDefault()

		if (e.currentTarget.files?.[0]) {
			this.saveVideo(e.currentTarget.files?.[0])
		}
	}

	private saveVideo = async (file: File) => {
		this.setState({
			videoLoading: true,
			videoError: null,
		})
		if (file) {
			const formData = new FormData()
			formData.append('file', file)
			try {
				const result = await apiFetch(
					'/videos',
					{
						method: 'POST',
						body: formData,
					},
					{},
				)
				const resp = (await result.json()) as ApiResponse<Video>
				if (resp && resp.status === 'OK' && resp.data) {
					this.setValue('videoId', resp.data.id)
					this.setValue('videoPath', resp.data.path)
				}
				this.setState({
					videoLoading: false,
					videoError: resp?.status === 'error' ? resp.message ?? 'Unknown error when uploading video.' : null,
				})
			} catch (error) {
				captureExceptionWithSentry(error)
				this.setState({
					videoLoading: false,
				})
			}
		}
	}

	private renderImage = () => {
		return (
			<FormGroup
				controlId={`image-${this.props.articleUid}-${this.props.variation.variation_no}`}
				validationState={this.props.variation.image ? 'success' : 'error'}
			>
				<Col md={12}>
					<ControlLabel style={{ width: '100%' }}>
						{this.props.variation.image !== '' ? (
							<img
								data-test="variation-image"
								src={this.props.variation.image}
								width="100%"
								onError={() => {
									this.setValue('image', '')
									this.setState({
										imageHeight: undefined,
										imageWidth: undefined,
									})
								}}
								className="img-thumbnail"
								style={{ cursor: 'pointer', border: 0, padding: '0px' }}
								onLoad={(img: any) => {
									if (img.target && !this.state.imageHeight && !this.state.imageWidth) {
										this.setState({
											imageHeight: img.target.naturalHeight,
											imageWidth: img.target.naturalWidth,
										})
									}
								}}
							/>
						) : (
							<Panel
								style={{
									cursor: 'pointer',
									width: '100%',
									paddingTop: '80px',
									minHeight: '200px',
									maxHeight: '200px',
								}}
								className="text-center"
							>
								{this.state.imageLoading ? <LoadIndicator /> : 'Upload an Image'}
							</Panel>
						)}
					</ControlLabel>
					<FormControl
						style={{
							opacity: 0,
							position: 'absolute',
							pointerEvents: 'none',
							width: '1px',
							height: '1px',
						}}
						type="file"
						placeholder="Upload image"
						onChange={this.setImage}
						data-test="image_upload"
					/>
				</Col>
			</FormGroup>
		)
	}
	private setTitle = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		this.setValue('title', e.currentTarget.value)
	}
	private renderTitle = () => {
		return (
			<VariationItem
				name="title"
				type="text"
				componentClass="textarea"
				placeholder="Article Title"
				value={this.props.variation.title}
				onChange={this.setTitle}
				editable={this.state.editing}
				textElement="strong"
				textStyle={{ fontSize: '18px' }}
				toggleEditable={this.toggleEditable}
				maxLength={VariationLimits.title}
				minLength={VariationLimits.minTitle}
				focus={this.state.focusedElement === 'title'}
				suggestion={this.props.variation.suggestion}
				showSuggestionDropdown={true}
				suggestions={this.getSuggestions('title')}
				suggestionIndex={this.props.variation.variation_no - 1}
				closeSuggestionPopupExcept={this.state.closeSuggestionPopupExcept}
				closeAllSuggestionsPopupsExcept={this.handleCloseAllAiPopupsExcept.bind(this)}
				readonly={this.props.readonly}
			/>
		)
	}
	private setAdvertiser = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		this.setValue('advertiser_name', e.currentTarget.value)
	}
	private renderAdvertiser = () => {
		return (
			<VariationItem
				name="advertiser"
				type="text"
				placeholder="Advertiser Name"
				value={this.props.variation.advertiser_name}
				onChange={this.setAdvertiser}
				editable={this.state.editing}
				textClass="text-primary"
				toggleEditable={this.toggleEditable}
				maxLength={VariationLimits.advertiser_name}
				minLength={VariationLimits.min_advertiser_name}
				focus={this.state.focusedElement === 'advertiser'}
				suggestion={this.props.variation.suggestion}
				readonly={this.props.readonly}
			/>
		)
	}
	private setDescription = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		this.setValue('description', e.currentTarget.value)
	}
	private renderDescription = () => {
		return (
			<VariationItem
				name="description"
				type="text"
				componentClass="textarea"
				placeholder="Article Summary"
				value={this.props.variation.description}
				onChange={this.setDescription}
				editable={this.state.editing}
				toggleEditable={this.toggleEditable}
				maxLength={VariationLimits.summary}
				minLength={VariationLimits.minSummary}
				focus={this.state.focusedElement === 'description'}
				suggestion={this.props.variation.suggestion}
				showSuggestionDropdown={true}
				suggestions={this.getSuggestions('summary')}
				suggestionIndex={this.props.variation.variation_no - 1}
				closeSuggestionPopupExcept={this.state.closeSuggestionPopupExcept}
				closeAllSuggestionsPopupsExcept={this.handleCloseAllAiPopupsExcept.bind(this)}
				readonly={this.props.readonly}
			/>
		)
	}
	private setCta = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
		this.setValue('cta', e.currentTarget.value)
	}
	private renderCta = () => {
		return (
			<VariationItem
				name="cta"
				type="text"
				placeholder="Call to Action"
				value={this.props.variation.cta}
				onChange={this.setCta}
				editable={this.state.editing}
				textClass="text-primary"
				toggleEditable={this.toggleEditable}
				maxLength={VariationLimits.max_cta}
				minLength={VariationLimits.min_cta}
				focus={this.state.focusedElement === 'cta'}
				suggestion={this.props.variation.suggestion}
				showSuggestionDropdown={true}
				suggestions={this.getSuggestions('call_to_action')}
				suggestionIndex={this.props.variation.variation_no - 1}
				closeSuggestionPopupExcept={this.state.closeSuggestionPopupExcept}
				closeAllSuggestionsPopupsExcept={this.handleCloseAllAiPopupsExcept.bind(this)}
				readonly={this.props.readonly}
			/>
		)
	}

	private handleUrlChange = (e: any) => {
		const valueToChange = this.props.modalName === 'ClickTargetModal' ? 'targetUrl' : 'impressionPixel'
		// Replace possible newlines with empty string as those can break the url
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		const url = e.target.value.replace('\n', '')
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		this.setValue(valueToChange, url)
	}

	private keepVariation = () => {
		if (this.props.updateVariationField) {
			this.props.updateVariationField(
				this.props.articleUid,
				this.props.variation.variation_no,
				'suggestion',
				false,
				this.props.instant,
			)
		}
		this.props.handleCloseModal()
	}

	private discardVariation = () => {
		if (this.props.removeVariation) {
			this.props.removeVariation(this.props.articleUid, this.props.variation.variation_no, this.props.instant)
		}
		this.props.handleCloseModal()
	}
	private renderSuggestedModal = () => {
		return (
			<Col className="text-center" style={{ paddingTop: '20px', paddingBottom: '20px' }}>
				<p style={{ fontSize: '16px' }}>
					<strong>
						It is recommended to have at least three variations for each article. We have prefilled this
						variation.
					</strong>
				</p>
				<p>Would you like to edit this variation?</p>
				<ButtonToolbar>
					<Button bsStyle="success" bsSize="small" onClick={this.keepVariation} style={{ float: 'none' }}>
						Edit
					</Button>
					<Button bsSize="small" onClick={this.discardVariation} style={{ float: 'none' }}>
						Discard
					</Button>
				</ButtonToolbar>
			</Col>
		)
	}
	private renderModal = () => {
		switch (this.props.modalName) {
			case 'ClickTargetModal':
			case 'ImpressionPixelModal':
				return this.renderInputModal()
			case 'SelectMediasModal':
				return this.renderSelectMediasModal()
		}
	}

	private validateImpressionUrlWithWarning() {
		return (
			probablyClickTrackers.filter(
				(domain) =>
					this.props.variation.impressionPixel && this.props.variation.impressionPixel.indexOf(domain) > -1,
			).length > 0
		)
	}

	private renderInputModal = () => {
		return (
			<FormGroup
				controlId={this.props.modalName}
				validationState={
					this.props.modalName === 'ClickTargetModal'
						? validateClickUrl(this.props.variation.targetUrl)
						: validateImpressionUrl(this.props.variation.impressionPixel)
				}
			>
				<Col md={12}>
					<FormControl
						type="text"
						componentClass="textarea"
						placeholder={
							this.props.modalName === 'ClickTargetModal'
								? 'Click Target URL'
								: 'Impression Tracking Pixel URL(s) (optional)'
						}
						value={
							this.props.modalName === 'ClickTargetModal'
								? this.props.variation.targetUrl
								: this.props.variation.impressionPixel
						}
						onChange={this.handleUrlChange}
						data-test="urlfield"
					/>
					{this.props.modalName === 'ImpressionPixelModal' && (
						<>
							<HelpBlock style={{ color: 'gray' }}>Separate multiple URLs with a comma.</HelpBlock>
							<HelpBlock style={{ color: 'gray' }}>
								Only pixels from{' '}
								<a target="_blank" rel="noreferrer" href="https://iabeurope.eu/vendor-list-tcf/">
									IAB TCF-registered vendors
								</a>{' '}
								are allowed. Make sure to include{' '}
								<a
									target="_blank"
									rel="noreferrer"
									href="https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20Consent%20string%20and%20vendor%20list%20formats%20v2.md#full-tc-string-passing"
								>
									GDPR macros.
								</a>
							</HelpBlock>
						</>
					)}
					{this.props.modalName === 'ImpressionPixelModal' && this.validateImpressionUrlWithWarning() && (
						<Alert bsStyle="warning" style={{ marginTop: '16px' }}>
							<strong>
								WARNING: Please check the Impression Tracking Pixel URL. Value might contain Click
								Tracker URL instead.
							</strong>
						</Alert>
					)}
					{this.props.modalName === 'ClickTargetModal' && (
						<>
							<HelpBlock style={{ color: 'gray' }}>
								Only click trackers from{' '}
								<a target="_blank" rel="noreferrer" href="https://iabeurope.eu/vendor-list-tcf/">
									IAB TCF-registered vendors
								</a>{' '}
								are allowed. Make sure to include the{' '}
								<a
									target="_blank"
									rel="noreferrer"
									href="https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20Consent%20string%20and%20vendor%20list%20formats%20v2.md#full-tc-string-passing"
								>
									GDPR macros.
								</a>
							</HelpBlock>
							<HelpBlock>
								<InfoPopup
									title="Tracking Tokens"
									placement="bottom"
									trigger={['click']}
									element={<a style={{ cursor: 'pointer' }}>Click for tracking tokens</a>}
								>
									{[
										{
											token: 'timestamp',
											description: 'Current timestamp',
										},
										{
											token: 'ad_title',
											description: 'Title of the ad',
										},
										{
											token: 'ad_variation',
											description: 'Variation number of the ad',
										},
										{
											token: 'camp_name',
											description: 'Name of the campaign',
										},
										{
											token: 'site',
											description: 'Name of the website',
										},
										{
											token: 'device',
											description: 'Device used',
										},
									].map((item) => {
										return (
											<Row key={item.token}>
												<Col xs={5}>
													<strong>{`{${item.token}}`}</strong>
												</Col>
												<Col xs={7}>{item.description}</Col>
											</Row>
										)
									})}
									<br />
									<Row>
										<Col
											xs={12}
											style={{
												borderTopWidth: '1px',
												borderTopColor: '#e5e5e5',
												borderTopStyle: 'solid',
												paddingTop: '5px',
											}}
										>
											{`Usage e.g. https://url.com?ts={timestamp}`}
										</Col>
									</Row>
								</InfoPopup>
							</HelpBlock>
						</>
					)}
				</Col>
			</FormGroup>
		)
	}
	private renderSelectMediasModal = () => {
		return (
			<>
				<strong>Choose which medias this ad variation will be displayed on.</strong>
				{this.props.selectedLocations?.map((loc) => {
					return (
						<Checkbox
							key={loc.locid}
							checked={
								!this.props.variation.hidden_locations ||
								!this.props.variation.hidden_locations.includes(loc.locid)
							}
							onChange={() => this.toggleLocationHidden(loc.locid)}
						>
							{loc.title}
						</Checkbox>
					)
				})}
			</>
		)
	}
	private toggleLocationHidden = (locId: number) => {
		if (!this.props.variation.hidden_locations || this.props.variation.hidden_locations.length === 0) {
			this.setValue('hidden_locations', [locId])
		} else {
			const index = this.props.variation.hidden_locations.indexOf(locId)
			if (index > -1) {
				const hiddenLocations = [...this.props.variation.hidden_locations]
				hiddenLocations.splice(index, 1)
				this.setValue('hidden_locations', hiddenLocations)
			} else {
				const hiddenLocations = [...this.props.variation.hidden_locations]
				hiddenLocations.push(locId)
				this.setValue('hidden_locations', hiddenLocations)
			}
		}
	}

	private body: any = undefined
	private setRef = (ref: React.Component) => {
		if (!this.body) {
			this.body = ref
			this.forceUpdate()
		}
	}
	private activateVariation = () => {
		if (this.props.updateVariationField) {
			this.props.updateVariationField(
				this.props.articleUid,
				this.props.variation.variation_no,
				'is_active',
				true,
				this.props.instant,
			)
		}
	}
	private deactivateVariation = () => {
		if (this.props.updateVariationField) {
			this.props.updateVariationField(
				this.props.articleUid,
				this.props.variation.variation_no,
				'is_active',
				false,
				this.props.instant,
			)
		}
	}
	private renderActivateDeactive = () => {
		if (!this.props.variation.suggestion && this.props.readonly) {
			return (
				<Row style={{ marginTop: '10px' }}>
					<Col xs={12}>
						This ad variation is <strong>{this.props.variation.is_active ? '' : 'not'}</strong> active
					</Col>
				</Row>
			)
		}

		if (!this.props.variation.suggestion && !this.props.readonly) {
			return (
				<Row style={{ marginTop: '10px' }}>
					<Col xs={12}>
						<strong>This ad variation is </strong>
						<ButtonGroup justified>
							<ButtonGroup>
								<Button
									key={1}
									bsSize="xs"
									bsStyle={this.props.variation.is_active ? 'success' : 'default'}
									onClick={() => !this.props.variation.is_active && this.activateVariation()}
									active={this.props.variation.is_active}
								>
									{this.props.variation.is_active ? 'Active' : 'Activate'}
								</Button>
							</ButtonGroup>
							<ButtonGroup>
								<Button
									key={2}
									bsSize="xs"
									bsStyle={!this.props.variation.is_active ? 'warning' : 'default'}
									onClick={() => this.props.variation.is_active && this.deactivateVariation()}
									active={!this.props.variation.is_active}
								>
									{!this.props.variation.is_active ? 'Deactive' : 'Deactivate'}
								</Button>
							</ButtonGroup>
						</ButtonGroup>
					</Col>
				</Row>
			)
		}
	}

	private renderApproveDismissEditComment = () => {
		if (!this.props.managingAiSuggestions) {
			return <></>
		}

		return (
			<>
				<Row style={{ marginTop: '10px' }}>
					<Col
						xs={12}
						style={{ display: 'flex', justifyContent: 'space-around' }}
						className="aiVariationButtons"
					>
						<button
							className="approve"
							onClick={() =>
								this.props.approveOrDisapproveAiSuggestion &&
								this.props.approveOrDisapproveAiSuggestion(this.props.variation, true)
							}
						>
							Approve
						</button>
						<button
							className="disapprove"
							onClick={() =>
								this.props.approveOrDisapproveAiSuggestion &&
								this.props.approveOrDisapproveAiSuggestion(this.props.variation, false)
							}
						>
							Dismiss
						</button>
						<button className={this.state.editing ? 'editing' : ''} onClick={() => this.toggleEditable()}>
							Edit
						</button>
						<button onClick={() => this.toggleComment()}>
							Comments
							{(this.props.variation.comment || '').trim().length > 0 && (
								<Icon
									icon={IconType.Comment}
									style={{
										marginLeft: '1rem',
										color: this.isCommentNew() ? 'red' : 'black',
									}}
								/>
							)}
						</button>
					</Col>
				</Row>
				{this.state.showComment && (
					<Row style={{ marginTop: '10px' }}>
						<Col xs={12} style={{ display: 'flex', justifyContent: 'space-around' }}>
							<FormControl
								type="text"
								componentClass="textarea"
								value={this.props.variation.comment || ''}
								onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) =>
									this.setValue('comment', e.currentTarget.value)
								}
							/>
						</Col>
					</Row>
				)}
			</>
		)
	}
	private renderStats = () => {
		if (
			!this.props.onDashboard &&
			this.props.variation.ctr !== undefined &&
			this.props.variation.clicks !== undefined
		) {
			return (
				<Row style={{ marginTop: '10px' }}>
					<Col xs={12}>
						<strong>CTR: </strong>
						{Math.round(this.props.variation.ctr * 100 * 100) / 100} % <strong>Clicks: </strong>
						{this.props.variation.clicks}
					</Col>
				</Row>
			)
		}
	}
	private renderVideoError() {
		return (
			this.state.videoError && (
				<div style={{ display: 'flex', marginTop: '20px' }}>
					<div
						style={{
							marginTop: 0,
							fontSize: '1.5em',
							paddingRight: '10px',
						}}
					>
						<i className="fa fa-exclamation-triangle text-danger" />
					</div>
					<div>
						<p className="text-danger">
							<b>{this.state.videoError}</b>
						</p>
					</div>
				</div>
			)
		)
	}
	private renderImageSizeWarning() {
		// readonly mode (=for example approval), we can't do anything about the content so don't show errors
		if (this.props.readonly) {
			return null
		}

		return (
			!this.props.variation.suggestion &&
			((this.state.imageWidth !== undefined && this.state.imageWidth < 1200) ||
				(this.state.imageHeight !== undefined && this.state.imageHeight < 627)) && (
				<div style={{ display: 'flex', marginTop: '20px' }}>
					<div
						style={{
							marginTop: 0,
							fontSize: '1.5em',
							paddingRight: '10px',
						}}
					>
						<i className="fa fa-exclamation-triangle text-danger" />
					</div>
					<div>
						<p className="text-danger">
							<b>
								WARNING: image does not follow guidelines. This ad will not display in some media
								placements. Recommended minimum image size is 1200x628.
							</b>
						</p>
						<p>
							Click on the image to select another. Read more about proper image use{' '}
							<a href="https://get.readpeak.com/proper-image/" target="_blank" rel="noreferrer">
								here.
							</a>
						</p>
					</div>
				</div>
			)
		)
	}
	private renderImgError() {
		// readonly mode (=for example approval), we can't do anything about the content so don't show errors
		if (this.props.readonly) {
			return null
		}

		return (
			!this.props.variation.suggestion &&
			this.state.imgError && (
				<div style={{ display: 'flex', marginTop: '20px' }}>
					<div
						style={{
							marginTop: 0,
							fontSize: '1.5em',
							paddingRight: '10px',
						}}
					>
						<i className="fa fa-exclamation-triangle text-danger" />
					</div>
					<div>
						<p className="text-danger">
							<b>
								WARNING: Error processing the image. Make sure the image is a proper image file and the
								file size does not exceed 10 MBs.
							</b>
						</p>
						<p>
							Click on the image to select another. Read more about proper image use{' '}
							<a href="https://get.readpeak.com/proper-image/" target="_blank" rel="noreferrer">
								here.
							</a>
						</p>
					</div>
				</div>
			)
		)
	}
	private stringToHex(input: string) {
		let result = ''
		for (let i = 0; i < input.length; i++) {
			result += input
				.charCodeAt(i)
				.toString(16)
				.padStart(2, '0')
		}
		return result
	}
	private renderTestClickTargetUrl() {
		if (this.props.variation.targetUrl) {
			let url = this.props.variation.targetUrl
			if (!url.startsWith('http')) {
				url = `https://${url}`
			}
			const location = 0
			const article = 0
			const deviceType = 100

			const idPartial = `${this.props.campaignId}:${location}:${article}:${deviceType}`
			const bidId = 0
			const hasQueryParams = /\?/
			const sign = hasQueryParams.test(url) ? '&' : '?'
			const hashTag = url.includes('#') ? '#' + url.split('#')[1] : ''
			url = url.split('#')[0]

			const clickIdQueryParameter = `${sign}rpcid=${bidId}I${this.stringToHex(idPartial)}`
			let testurl = `${url}${clickIdQueryParameter}${hashTag}`
			if (this.props.instant && this.props.articleId && this.props.campaignId) {
				testurl = this.appendInstantArticleParam(testurl, this.props.articleId, this.props.campaignId)
			}
			return (
				<a href={testurl} target="_blank" rel="noreferrer" data-test="testurl">
					Test ad click target URL here
				</a>
			)
		} else if (!this.props.variation.suggestion) {
			if (this.props.instant) {
				this.props.handleShowModal('ClickTargetModal')
			}
			return (
				<div>
					<span
						style={{
							marginTop: 0,
							fontSize: '1.5em',
							paddingRight: '10px',
						}}
					>
						<i className="fa fa-exclamation-triangle text-danger" />
					</span>
					<span className="text-danger">
						<b>WARNING: Click target URL is missing.</b>
					</span>
				</div>
			)
		}
	}

	private appendInstantArticleParam(url: string, article: number, campaign: number) {
		let mark = `?`
		if (url.includes('?')) {
			mark = '&'
		}
		const instantParam = this.generateInstantArticleParam(article, campaign)
		let rv = `${url}${mark}rpinstant=${instantParam}`
		if (url.includes('#')) {
			const urlParts = url.split('#')
			const urlPartOne = `${urlParts[0]}${mark}rpinstant=${instantParam}`
			rv = `${urlPartOne}#${urlParts[1]}`
		}

		return rv
	}

	private generateInstantArticleParam(articleId: number, campaign: number) {
		return `0I0I0I0I${articleId}I${campaign}I1I0I${this.props.client?.id || 0}`
	}

	public render() {
		return (
			<>
				<Panel className="VariationPanel" style={{ border: 0, boxShadow: 'none' }}>
					<Panel.Heading style={{ backgroundColor: 'transparent', border: 0 }}>
						{!this.props.managingAiSuggestions && (
							<>
								{this.panelHeader()}
								{this.renderStats()}
								{this.renderActivateDeactive()}
								{this.renderVideoError()}
								{this.renderImageSizeWarning()}
								{this.renderImgError()}
							</>
						)}
						{this.props.managingAiSuggestions && <>{this.renderApproveDismissEditComment()}</>}
					</Panel.Heading>
					<Panel.Body
						className="custom-modal-container"
						style={{
							backgroundColor: '#FCFCFC',
							border: '1px solid #ddd',
							borderRadius: '2px',
							margin: '10px',
						}}
						ref={this.setRef}
					>
						{this.renderThemeRow()}
						{this.props.adFormat && this.props.adFormat === AdFormats.Video && this.renderVideo()}
						{this.props.adFormat !== AdFormats.Video && this.renderImage()}
						{this.renderTitle()}
						{this.renderAdvertiser()}
						{this.renderDescription()}
						{this.renderCta()}

						{this.props.showModal && this.body !== undefined && (
							<StaticModal
								modalName={this.props.modalName}
								handleClose={this.props.handleCloseModal}
								title={this.props.modalTitle}
								footer={!this.props.variation.suggestion}
								noFooterBorder={true}
								container={this.body}
							>
								{this.props.variation.suggestion ? this.renderSuggestedModal() : this.renderModal()}
							</StaticModal>
						)}
					</Panel.Body>
					<Panel.Footer style={{ backgroundColor: 'transparent', border: 0 }}>
						{this.renderTestClickTargetUrl()}
					</Panel.Footer>
				</Panel>
			</>
		)
	}
}

export default VariationPanel
